import React from 'react';

/**
 * Supporter entry points (Paper AA7-0 + 00 Decisions: 'Avatar row ("Amina, Sam and 212 others";
 * falls back to "214 donors")'). The threshold reads the TOTAL the list reports:
 * - total >= 3 and >= 3 public (named, not anonymous) supporters → the named row + modal;
 * - total >= 3 but fewer than 3 public (incl. "show all supporters anonymous") → the count-only
 *   row ("214 donors", placeholder avatars, no names), opening the modal only when it has rows;
 * - total < 3 → the "Be the first to give ›" link, no modal.
 */
export const SUPPORTER_ROW_MIN_COUNT = 3;

/** How many of the supporters the page has are public — named, not anonymous. */
export function countPublicSupporters(supporters = []) {
  return (Array.isArray(supporters) ? supporters : []).filter((s) => s?.public).length;
}

/**
 * Which entry point a supporters summary gets.
 * @returns {'row'|'count-row'|'first-gift'|null}
 */
export function supportersEntryPoint(supporters = [], { total, canGive = true } = {}) {
  const list = Array.isArray(supporters) ? supporters : [];
  const count = Number.isFinite(total) ? total : list.length;
  if (count < SUPPORTER_ROW_MIN_COUNT) return canGive ? 'first-gift' : null;
  return countPublicSupporters(list) >= SUPPORTER_ROW_MIN_COUNT ? 'row' : 'count-row';
}

const AVATAR_FILLS = ['var(--cs-avatar-1)', 'var(--cs-avatar-2)', 'var(--cs-avatar-3)'];
const AVATAR_COUNT = AVATAR_FILLS.length;

const firstName = (name) => (typeof name === 'string' ? name.trim().split(/\s+/)[0] || '' : '');

const initialsOf = (name) => {
  if (typeof name !== 'string') return '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  return (words.length === 1 ? words[0][0] : `${words[0][0]}${words[1][0]}`).toUpperCase();
};

/**
 * "Amina, Sam and 212 others" — the first two public first names, then the rest as a count.
 * Falls back to "214 donors" when fewer than two names are public (Paper "Supporter avatar
 * row"). `noun` lets a sponsorship page say "sponsors".
 */
export function supportersNamesLine(publicNames, total, noun = 'donors') {
  const names = publicNames.map(firstName).filter(Boolean);
  if (names.length < 2) return `${total.toLocaleString('en-US')} ${noun}`;
  const others = total - 2;
  if (others <= 0) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${others.toLocaleString('en-US')} ${others === 1 ? 'other' : 'others'}`;
}

/**
 * SupporterAvatarRow — three overlapping 28 avatars, the names line and a chevron; opens the
 * supporters modal (CON-518, Paper "Supporter avatar row"). Presentational: the caller owns
 * the data and decides visibility (hidden below 3 supporters, never on events).
 *
 * @param {Array<{name: string, public: boolean}>} supporters - Newest or top first; `name` is the
 *   name as displayed ("Anonymous" for a private gift), `public` whether it is a real name.
 * @param {number} total - Everyone who gave.
 * @param {Function} [onOpen] - Opens the supporters modal / sheet; without it the row is static.
 * @param {string} [noun='donors']
 * @param {boolean} [countOnly=false] - Fewer than 3 public names: "214 donors", no names or initials.
 */
function SupporterAvatarRow({ supporters = [], total = 0, onOpen, noun = 'donors', countOnly = false }) {
  const publicNames = countOnly ? [] : supporters.filter((s) => s.public).map((s) => s.name);
  const line = supportersNamesLine(publicNames, total, noun);
  // Count-only: three placeholder discs in the avatar fills, no initials (no names to show).
  const avatars = countOnly
    ? Array.from({ length: AVATAR_COUNT }, () => ({ name: '' }))
    : supporters.slice(0, AVATAR_COUNT);
  const interactive = typeof onOpen === 'function';
  const Root = interactive ? 'button' : 'span';
  const rootProps = interactive ? { type: 'button', onClick: onOpen, 'aria-haspopup': 'dialog' } : {};

  return (
    <Root
      {...rootProps}
      data-cs-supporter-row={countOnly ? 'count' : 'named'}
      className="group inline-flex w-fit items-center gap-[var(--cs-space-2)] rounded-[var(--cs-control-radius)] bg-transparent p-0 text-left focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)]"
    >
      <span aria-hidden="true" className="flex shrink-0">
        {avatars.map((supporter, index) => (
          <span
            key={index}
            className={`flex size-[var(--cs-size-avatar)] items-center justify-center rounded-[var(--cs-radius-full)] border-2 border-[color:var(--cs-surface-0)] text-[length:var(--cs-text-2xs-plus)] leading-[var(--cs-leading-14)] font-semibold text-[color:var(--cs-text-secondary)] ${index > 0 ? '-ml-[var(--cs-space-2)]' : ''}`}
            style={{ backgroundColor: AVATAR_FILLS[index] }}
          >
            {initialsOf(supporter.name)}
          </span>
        ))}
      </span>
      <span className="text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-medium text-[color:var(--cs-text-primary)] group-hover:underline">
        {line}
      </span>
      {interactive && (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="size-[var(--cs-icon)] shrink-0 text-[color:var(--cs-text-tertiary)] [stroke-width:var(--cs-icon-stroke)]"
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
      )}
    </Root>
  );
}

/**
 * SupporterFirstGiftLink — what replaces the avatar row below three public supporters
 * (Paper AA7-0, on 84A-0): "Be the first to give" 14/20 medium in the brand text role (the action colour, near-black on light brands) and a
 * 16px chevron, 4px apart, underlined (SPEC F2: links are always underlined). It opens the form (the drawer on mobile), never the modal.
 *
 * @param {Function} onGive - Opens the form / checkout entry.
 * @param {string} [label='Be the first to give']
 */
export function SupporterFirstGiftLink({ onGive, label = 'Be the first to give' }) {
  return (
    <button
      type="button"
      onClick={onGive}
      data-cs-first-gift-link=""
      className="inline-flex w-fit items-center gap-[var(--cs-space-1)] rounded-[var(--cs-control-radius)] bg-transparent p-0 text-left text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-medium text-[color:var(--cs-brand-text,var(--cs-text-primary))] underline decoration-1 [text-underline-position:from-font] focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)]"
    >
      {label}
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="size-[var(--cs-icon)] shrink-0 [stroke-width:var(--cs-icon-stroke)]"
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </button>
  );
}

export default SupporterAvatarRow;
